import RadioGroup from '@mui/material/RadioGroup';
import { useMemo, useRef, useState, type VoidFunctionComponent } from 'react';
import type { SortingRule } from 'react-table';

import { useOnConfirm } from '../../../../../contexts/DialogContext';
import { window } from '../../../../../globals';
import { useOnUnmount } from '../../../../../hooks';
import { isTrue } from '../../../../../utils';
import {
  BUTTON_SIZE,
  ErrorAlert,
  IconButton,
  icons,
  LabeledRadio,
  RouterLink,
  Select,
} from '../../../../generic';
import Button from '../../../../generic/Button';
import Flex from '../../../../generic/Flex';
import Loader from '../../../../generic/Loader';
import SimpleReactTable from '../../../../generic/SimpleReactTable';
import TableHeader from '../../../../generic/TableHeader';
import { ExpirationCell, TokenCell, TokenNameCell } from '../../../../shared/portalTokenTable/cells';
import { usePortalUrl } from '../../../../shared/publicPortal/hooks';
import type { PortalToken } from '../../../../../../shared/publicPortal/types';
import type { SelectOption } from '../../../../../types';

import { usePortalTokens } from './hooks';
import PortalSettingsBanner from './PortalSettingsBanner';
import { PortalSettingsHeaderFullPage, PortalSettingsHeaderInline } from './PortalSettingsHeader';

import styles from './ProjectGroupPortalSettings.module.less';

type BaseProps = {
  projectGroupId?: number;
  onAddToken: (tokenId: number) => void;
  onRemoveToken: (tokenId: number) => void;
  publicOnPortal: boolean;
  setPublicOnPortal: (publicOnPortal: boolean) => void;
};

type InlineHeaderProps = BaseProps & {
  isOnFullPage: false;
};

type FullPageProps = BaseProps & {
  isOnFullPage: true;
  isDirty: boolean;
  onSave: () => Promise<void>;
};

export type PortalTokenSettingsProps = InlineHeaderProps | FullPageProps;

const defaultSortMode: SortingRule = { id: 'createdAt', desc: true };

const ActionsCell: VoidFunctionComponent<{ original: PortalToken; onRemoveToken: (tokenId: number) => void }> = ({
  original,
  onRemoveToken,
}) => (
  <IconButton
    icon={icons.xMark}
    id="delete"
    onClick={() => {
      onRemoveToken(original.id);
    }}
  />
);

/**
 * ProjectGroupTokenSettings is a component for managing the portal settings of a project group.
 */
const ProjectGroupTokenSettings: VoidFunctionComponent<PortalTokenSettingsProps> = (props) => {
  const onConfirm = useOnConfirm();
  const organizationId = window.user?.organizationId as number;
  const {
    availableTokenOptions,
    isTokenExpired,
    existingTokens,
    isLoading: isLoadingPortalTokenOptions,
    errors: portalTokenOptionErrors,
    onAddToken: onAddTokenToTable,
    onRemoveToken: onRemoveTokenFromTable,
  } = usePortalTokens(organizationId, props.projectGroupId);
  const alertProps = useMemo(() => ({ errors: [...(portalTokenOptionErrors || [])] }), [portalTokenOptionErrors]);
  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode);
  const [token, setToken] = useState<SelectOption<number> | null>(null);
  const removeDialogCallbackRef = useRef<(() => void) | null>(null);
  const { url: portalUrl } = usePortalUrl();

  // Remove the dialog when the component is unmounted.
  useOnUnmount(() => {
    if (removeDialogCallbackRef.current) {
      removeDialogCallbackRef.current();
    }
  });

  if (isLoadingPortalTokenOptions) {
    return <Loader />;
  }

  return (
    <>
      {props.isOnFullPage && (
        <PortalSettingsHeaderFullPage
          portalUrl={portalUrl}
          isDirty={props.isDirty}
          onSave={props.onSave}
        />
      )}
      <div className={styles.portalSettings}>
        {!props.isOnFullPage && <PortalSettingsHeaderInline portalUrl={portalUrl} />}
        <ErrorAlert status={alertProps} />
        <div className={styles.section}>
          <div className={styles.subTitle}>Privacy</div>
          <RadioGroup
            name="privacy"
            defaultValue={props.publicOnPortal.toString()}
            onChange={(event) => {
              props.setPublicOnPortal(isTrue(event.target.value));
            }}
            className={styles.privacyRadioGroup}
          >
            {/* The radio buttons set the publicOnPortal field in the DB */}
            <LabeledRadio value="true" label={<div className={styles.label}>Public</div>} />
            <LabeledRadio value="false" label={<div className={styles.label}>Private</div>} />
          </RadioGroup>
          {props.publicOnPortal && (
            <PortalSettingsBanner displayText="SBOMs published to the portal will be publicly available" />
          )}
        </div>
        {!props.publicOnPortal && (
          <div className={styles.section}>
            <div className={styles.subTitle}>Access Tokens</div>
            <div className={styles.description}>
              Add tokens to grant access to published SBOMs. Create tokens in{' '}
              <span className={styles.settingsLink}>
                <RouterLink to="/account/settings/organization/portal/tokens">Organization/Portal</RouterLink>.
              </span>
            </div>
            <div className={styles.addTokenControl}>
              <Flex gap="4px" align="center">
                <Select
                  className={styles.addTokenSelect}
                  id="addToken"
                  name="searchToken"
                  value={token}
                  options={availableTokenOptions || []}
                  onChange={setToken}
                  placeholder="Search by token name"
                />
                <Button
                  size={BUTTON_SIZE.MEDIUM}
                  onClick={() => {
                    if (!token) {
                      return;
                    }
                    const addToken = () => {
                      onAddTokenToTable(token.value);
                      props.onAddToken(token.value);
                      // Clear the token in the dropdown after adding it
                      setToken(null);
                    };

                    if (isTokenExpired(token.value)) {
                      removeDialogCallbackRef.current = onConfirm({
                        title: 'This token is expired',
                        body: (
                          <Flex gap="16px" direction="column" justify="flex-start" align="flex-start">
                            <div>
                              Users will not be able to use this token to access this release group on your portal
                              {' '}
                              because it is expired. You can change the expiry date of this token in your
                              {' '}
                              <span className={styles.settingsLink}>
                                <RouterLink to="/account/settings/organization/portal/tokens">
                                  organization settings
                                </RouterLink>
                              </span>
                              , after which users will be able to use the token again.
                            </div>
                            <div>Are you sure you want to add this token?</div>
                          </Flex>
                        ),
                        onConfirm: addToken,
                      });
                    } else {
                      addToken();
                    }
                  }}
                >
                  Add
                </Button>
              </Flex>
            </div>
            {existingTokens.length === 0 ? (
              <PortalSettingsBanner displayText="No tokens have been granted access" />
            ) : (
              <SimpleReactTable
                showPagination
                onSortedChanged={setSortMode}
                defaultSortMode={[defaultSortMode]}
                data={existingTokens || []}
                columns={[
                  {
                    id: 'name',
                    sortable: true,
                    Header: <TableHeader title="Name" sort={{ id: 'name', currentMode: sortMode }} />,
                    accessor: 'name',
                    Cell: TokenNameCell,
                    resizable: true,
                  },
                  {
                    id: 'token',
                    sortable: false,
                    Header: <TableHeader title="Token" />,
                    accessor: 'token',
                    Cell: TokenCell,
                    resizable: false,
                    width: 120,
                  },
                  {
                    id: 'expires',
                    sortable: true,
                    Header: <TableHeader title="Expires" sort={{ id: 'expires', currentMode: sortMode }} />,
                    accessor: 'expiration',
                    Cell: ExpirationCell,
                    resizable: false,
                    width: 96,
                  },
                  {
                    style: { justifyContent: 'center' },
                    id: 'actions',
                    sortable: false,
                    Header: <TableHeader title="Actions" center />,
                    accessor: 'id',
                    // eslint-disable-next-line react/no-unstable-nested-components
                    Cell: ({ original: PortalToken }) => (
                      <ActionsCell
                        original={PortalToken}
                        onRemoveToken={(tokenId: number) => {
                          onRemoveTokenFromTable(tokenId);
                          props.onRemoveToken(tokenId);
                        }}
                      />
                    ),
                    resizable: false,
                    width: 120,
                  },
                ]}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectGroupTokenSettings;
